import type { CustomerNotification } from '@/api/customer/customerNotificationsQuery';
import { useCustomerNotificationsQuery } from '@/hooks/queries/customer/useCustomerNotificationsQuery';

interface UseAppointmentRatingPromptEligibilityResult {
  /** The notification whose appointment should be shown for rating, or null. */
  pendingNotification: CustomerNotification | null;
  /** Ids of every pending notification (shown + rest) — dismissed together. */
  allNotificationIds: string[];
}

/**
 * Determines whether the auto rating prompt should be shown for a customer.
 *
 * Mirrors the legacy web behavior: driven by the customer's pending
 * `appointmentReview` notifications. When more than one is pending, the first
 * (latest) is shown and the rest are dismissed alongside it once the customer
 * rates or skips.
 */
export function useAppointmentRatingPromptEligibility(
  customerId: string,
): UseAppointmentRatingPromptEligibilityResult {
  const { data: notifications } = useCustomerNotificationsQuery({
    customerId,
    enabled: !!customerId,
  });

  // Guard against non-array shapes. React Query persistence can rehydrate a
  // cache entry left by an older app build whose shape differs from today's
  // response; because it's served fresh (within staleTime) the queryFn never
  // re-runs to correct it. Coerce any shape to an array before iterating so a
  // stale rehydrated value can't crash the destructure (SQR-13865).
  const notificationList: CustomerNotification[] = Array.isArray(notifications)
    ? notifications
    : [];

  const [pendingNotification = null] = notificationList;

  return {
    pendingNotification,
    allNotificationIds: notificationList.map((n) => n.id),
  };
}
